// Round ORG-2 (2026) — HR Data-Quality panel for the Organizational Chart
// (Issue 6 — legacy OrgChart.vb UpdateStats() parity, modernized).
//
// Shows: missing photos / titles / hire dates / extensions (the four legacy
// counters), PLUS missing supervisor, orphaned reporting chains, employees
// disconnected from the company tree, duplicate EmpIDs, and upcoming service
// anniversaries within a configurable window (default 60 days — the legacy
// hardcoded horizon, now user-adjustable).
//
// Authorization: this component is only MOUNTED for HR users (frontend
// convenience), but the real gate is server-side — GET /api/OrgChart/
// data-quality carries [Authorize(Policy = CanAccessHR)], so a direct API
// call by a non-HR user returns 403 and this panel shows the error message.
//
// Fixing the data: rows are informational; actual edits go through the
// existing, audited workflows (HR Dashboard → Employee drill-downs, Profile
// approvals). This panel deliberately does NOT edit employee data directly —
// editing from the chart would bypass the HR approval/audit pipeline.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Badge, Spinner } from "reactstrap";
import { Link } from "react-router-dom";
import api from "../../api";

const GROUPS = [
    { key: "missingPhotos",     label: "Missing photo",       icon: "ri-image-line" },
    { key: "missingTitles",     label: "Missing title",       icon: "ri-price-tag-3-line" },
    { key: "missingHireDates",  label: "Missing hire date",   icon: "ri-calendar-line" },
    { key: "missingExtensions", label: "Missing extension",   icon: "ri-phone-line" },
    { key: "missingSupervisor", label: "No supervisor",       icon: "ri-user-unfollow-line" },
    { key: "orphanedChain",     label: "Orphaned chain",      icon: "ri-link-unlink" },
    { key: "disconnected",      label: "Disconnected",        icon: "ri-error-warning-line" },
];

const OrgDataQualityPanel = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeGroup, setActiveGroup] = useState("missingPhotos");
    const [days, setDays] = useState(60);

    const load = useCallback((windowDays) => {
        setLoading(true);
        setError(null);
        api.get(`OrgChart/data-quality?days=${windowDays}`)
            .then((res) => setData(res.data))
            .catch((e) => {
                setError(
                    e?.response?.status === 403
                        ? "The data-quality report is restricted to HR."
                        : e?.response?.data?.message || e?.message || "Failed to load the data-quality report."
                );
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(60); }, [load]);

    const activeRows = useMemo(() => (data ? data[activeGroup] || [] : []), [data, activeGroup]);

    if (loading && !data) {
        return (
            <div className="odq-wrap text-center py-4">
                <Spinner size="sm" color="primary" />
                <span className="text-muted ms-2">Building the data-quality report…</span>
            </div>
        );
    }
    if (error) return <Alert color="danger" className="mt-2">{error}</Alert>;
    if (!data) return null;

    return (
        <div className="odq-wrap">
            <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                <h6 className="mb-0"><i className="ri-shield-check-line me-1" />Org Chart Data Quality <Badge color="light" className="border text-muted">HR only</Badge></h6>
                <div className="ms-auto text-muted small">
                    {data.totals?.employees} active employees checked
                </div>
            </div>

            {/* Counter tabs */}
            <div className="odq-tabs mb-2">
                {GROUPS.map((g) => {
                    const count = data.totals?.[g.key] ?? (data[g.key]?.length || 0);
                    return (
                        <button
                            key={g.key}
                            type="button"
                            className={`odq-tab ${activeGroup === g.key ? "active" : ""} ${count > 0 ? "has-items" : ""}`}
                            onClick={() => setActiveGroup(g.key)}
                        >
                            <i className={g.icon} /> {g.label}
                            <span className="odq-count">{count}</span>
                        </button>
                    );
                })}
            </div>

            {/* List for the selected counter */}
            <div className="odq-list">
                {activeRows.length === 0 && (
                    <div className="text-muted small py-2"><i className="ri-checkbox-circle-line me-1 text-success" />Nothing to fix in this category.</div>
                )}
                {activeRows.map((r) => (
                    <div key={`${activeGroup}-${r.empId}`} className="odq-row">
                        <span className="odq-name">{r.name || r.empId}</span>
                        <span className="odq-meta">#{r.empId}{r.title ? ` • ${r.title}` : ""}{r.department ? ` • Dept ${r.department}` : ""}</span>
                        {/* Edits go through the audited HR workflows, not the chart. */}
                        <Link to="/humanresource?section=employees&view=list" className="odq-fix" title="Open HR › Employees to fix this record">
                            Fix in HR <i className="ri-arrow-right-line" />
                        </Link>
                    </div>
                ))}
            </div>

            {data.duplicateEmpIds?.length > 0 && (
                <Alert color="warning" className="mt-2 py-2 small">
                    <strong>Duplicate EmpIDs detected:</strong>{" "}
                    {data.duplicateEmpIds.map((d) => `${d.empId} (×${d.count})`).join(", ")} — needs a data fix.
                </Alert>
            )}

            {/* Upcoming anniversaries (legacy 60-day list, window adjustable) */}
            <div className="d-flex align-items-center gap-2 mt-3 mb-1">
                <h6 className="mb-0"><i className="ri-cake-2-line me-1" />Upcoming Service Anniversaries</h6>
                <select
                    className="form-select form-select-sm w-auto"
                    value={days}
                    onChange={(e) => { const d = Number(e.target.value); setDays(d); load(d); }}
                    title="Look-ahead window"
                >
                    <option value={30}>Next 30 days</option>
                    <option value={60}>Next 60 days</option>
                    <option value={90}>Next 90 days</option>
                </select>
                {loading && <Spinner size="sm" color="primary" />}
            </div>
            <div className="odq-list">
                {(data.upcomingAnniversaries || []).length === 0 && (
                    <div className="text-muted small py-2">No anniversaries in the selected window.</div>
                )}
                {(data.upcomingAnniversaries || []).map((a) => (
                    <div key={`ann-${a.empId}-${a.date}`} className="odq-row">
                        <span className="odq-name">{a.name}</span>
                        <span className="odq-meta">
                            {new Date(a.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            {" • "}{a.years} Year{a.years === 1 ? "" : "s"}
                            {" • in "}{a.daysAway} day{a.daysAway === 1 ? "" : "s"}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OrgDataQualityPanel;
